import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody } from 'reactstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import TimePicker from 'rc-time-picker';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faClock, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import Validator from '../../common/forms/Validator';
import Close from '../../common/ui/Close';
import Loader from '../../common/ui/Loader';
import ErrorMessage from '../../common/forms/ErrorMessage';
import { monthDayYearWithSlashFormat, timeFormatUi, defaultTimeZone } from '../../../constants/date';
import { resolveDateTime } from '../../../helpers/jobs';
import { useTimezoneForDate } from '../../../helpers/timezones';
import FormActionButtons from '../../common/forms/FormActionButtons';

export default class CreateOrientationModal extends Validator {
  static propTypes = {
    locationsList: PropTypes.array.isRequired,
    history: PropTypes.object.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggleModal: PropTypes.func.isRequired,
    createOrientation: PropTypes.func.isRequired,
    showUpdatedOrientationsTab: PropTypes.func.isRequired
  };

  state = {
    isLoading: false,
    date: null,
    startTime: null,
    endTime: null,
    location: null
  };

  validationRules = {
    date: { required: true },
    startTime: { required: true },
    endTime: { required: true },
    location: { required: true }
  };

  handleChangeDateTime = (name, value) => {
    this.setState({
      [name]: value
    }, () => { this.setDateTimeHours(); });
  };

  setDateTimeHours = () => {
    const { date, startTime, endTime } = this.state;
    if (date && startTime && endTime) {
      const updatedData = resolveDateTime(date, startTime, endTime);
      this.setState({
        startTime: updatedData.startTime,
        endTime: updatedData.endTime
      });
    }
  };

  handleSelectLocation = location => {
    this.setState({ location });
  };

  closeCreateModal = () => {
    this._resetErrors();
    this.setState({
      date: null,
      startTime: null,
      endTime: null,
      location: null
    });
    this.props.toggleModal();
  };

  handleSubmit = event => {
    event.preventDefault();

    if (this.validate(this.validationRules)) {
      this.setState({ isLoading: true });
      const { startTime, endTime, location: { data } } = this.state;
      this.props.createOrientation({
        start_date: moment.utc(useTimezoneForDate(startTime, data.time_zone_id || defaultTimeZone)).format(),
        end_date: moment.utc(useTimezoneForDate(endTime, data.time_zone_id || defaultTimeZone)).format(),
        orientation_location_id: data.id
      }).then(data => {
        this.setState({ isLoading: false });
        if (data) {
          this.closeCreateModal();
          this.props.showUpdatedOrientationsTab();
        }
      });
    }
  };

  render () {
    const { isOpen, toggleModal, locationsList } = this.props;
    const { isLoading, date, startTime, endTime, location } = this.state;
    const options = locationsList.map(location => ({
      label: `${location.name}, ${location.address}`,
      value: location.id,
      data: location
    }));
    const errors = this.validationErrors;

    return (
      <Modal isOpen={isOpen}>
        <form onSubmit={this.handleSubmit} className={isLoading ? 'is-loading' : ''}>
          <div className='modal-header'>
            <div className='modal-title'>New Orientation</div>
            <Close onClick={toggleModal} />
          </div>
          <ModalBody>
            <div className='row align-items-center mb-4 mb-sm-3'>
              <label className='col-5 col-sm-3' htmlFor='new-orientation-datepicker'>
                <span className='modal-icon-wrapper'><FontAwesomeIcon icon={faCalendarAlt} /></span>
                Date
              </label>
              <div className='col-7 col-sm-9 d-flex align-items-center job-date'>
                <div className='input-picker-box'>
                  <DatePicker
                    id='new-orientation-datepicker'
                    className='input-picker'
                    dateFormat={monthDayYearWithSlashFormat}
                    onChange={date => this.handleChangeDateTime('date', date)}
                    selected={date}
                    minDate={moment()}
                    dropdownMode='select'
                    placeholderText='Select Date'
                  />
                </div>
              </div>
              <div className='col-6 offset-sm-3 col-sm-9 mt-1'>
                <ErrorMessage errorText={errors.date} />
              </div>
            </div>
            <div className='row align-center mb-4 mb-sm-3'>
              <label className='col-12 col-sm-3 mb-1 mb-sm-0'>
                <span className='modal-icon-wrapper'><FontAwesomeIcon icon={faClock} /></span>
                Time
              </label>
              <div className='col-12 col-sm-9 d-flex align-items-center'>
                <div className='input-picker-box'>
                  <TimePicker
                    showSecond={false}
                    className='create-job-timepicker-wrapper'
                    placeholder='Select Time'
                    onChange={startTime => this.handleChangeDateTime('startTime', startTime)}
                    format={timeFormatUi}
                    use12Hours
                    minuteStep={15}
                    value={startTime}
                    defaultOpenValue={moment().startOf('day')}
                  />
                </div>
                <div className='input-picker-delimiter text-center'>to</div>
                <div className='input-picker-box right'>
                  <TimePicker
                    showSecond={false}
                    className='create-job-timepicker-wrapper'
                    placeholder='Select Time'
                    onChange={endTime => this.handleChangeDateTime('endTime', endTime)}
                    format={timeFormatUi}
                    use12Hours
                    minuteStep={15}
                    value={endTime}
                    defaultOpenValue={moment().startOf('day')}
                  />
                </div>
              </div>
              { (errors.startTime || errors.endTime) && (
                <React.Fragment>
                  <div className='col-6 offset-sm-3 col-sm-4 mt-1 pr-sm-0'>
                    <ErrorMessage errorText={errors.startTime} />
                  </div>
                  <div className='col-6 offset-sm-1 col-sm-4 mt-1 pl-sm-0'>
                    <ErrorMessage errorText={errors.endTime} />
                  </div>
                </React.Fragment>
              )}
            </div>
            <div className='row align-items-center mb-4 mb-sm-3'>
              <label className='col-12 col-sm-3'>
                <span className='modal-icon-wrapper'><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                Location
              </label>
              <div className='col-12 col-sm-9 d-flex align-items-center'>
                <Select
                  className='w-100'
                  onChange={this.handleSelectLocation}
                  options={options}
                  placeholder='Choose'
                  value={location}
                />
              </div>
              <div className='col-6 offset-sm-3 col-sm-9 mt-1'>
                <ErrorMessage errorText={errors.location} />
              </div>
            </div>
            <FormActionButtons onClickCancel={this.closeCreateModal} submitTitle='Create' />
          </ModalBody>
        </form>
        { isLoading && <Loader /> }
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/orientations/CreateOrientationModal.js